import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, ChevronDown, ChevronUp, PlusCircle, XCircle } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { CLASSE_DE_CAMPO } from '@/features/admin/componentes';
import { formatarData, formatarDataHora } from '@/lib/format';
import {
  atualizarMelhoria,
  chavesCsi,
  criarMelhoria,
  excluirMelhoria,
  listarMelhorias,
  type CsiImprovement,
  type CsiPriority,
  type CsiSourceType,
  type CsiStatus,
} from '@/features/csi/csi.api';

const STATUS_COR: Record<CsiStatus, string> = {
  OPEN: 'bg-primary/10 text-primary ring-1 ring-inset ring-primary/20',
  IN_PROGRESS: 'bg-warning/15 text-warning ring-1 ring-inset ring-warning/30',
  CLOSED: 'bg-success/12 text-success ring-1 ring-inset ring-success/25',
  REJECTED: 'bg-muted text-muted-foreground',
};

const STATUS_PT: Record<CsiStatus, string> = {
  OPEN: 'Aberto',
  IN_PROGRESS: 'Em andamento',
  CLOSED: 'Concluido',
  REJECTED: 'Rejeitado',
};

const PRIORIDADE_PT: Record<CsiPriority, string> = {
  HIGH: 'Alta',
  MEDIUM: 'Media',
  LOW: 'Baixa',
};

const PRIORIDADE_COR: Record<CsiPriority, string> = {
  HIGH: 'text-destructive font-semibold',
  MEDIUM: 'text-warning',
  LOW: 'text-muted-foreground',
};

const ORIGEM_PT: Record<CsiSourceType, string> = {
  SLA_BREACH: 'Violacao de ANS',
  INCIDENT_REVIEW: 'Revisao de incidente',
  PIR: 'Revisao pos-implementacao',
  PROBLEM_CLOSED: 'Problema encerrado',
  SURVEY: 'Pesquisa de satisfacao',
  MANUAL: 'Manual',
};

const ORIGENS: CsiSourceType[] = [
  'MANUAL',
  'INCIDENT_REVIEW',
  'SLA_BREACH',
  'PROBLEM_CLOSED',
  'PIR',
  'SURVEY',
];

export function MelhoriasCsiPage() {
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<CsiStatus | undefined>(undefined);
  const [criando, setCriando] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: chavesCsi.lista(filtro),
    queryFn: () => listarMelhorias({ status: filtro }),
  });

  const abas: Array<{ valor: CsiStatus | undefined; label: string }> = [
    { valor: undefined, label: 'Todos' },
    { valor: 'OPEN', label: 'Abertos' },
    { valor: 'IN_PROGRESS', label: 'Em andamento' },
    { valor: 'CLOSED', label: 'Concluidos' },
  ];

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Registro de Melhorias (CSI)</h1>
        <Button onClick={() => setCriando((v) => !v)} icon={<PlusCircle className="h-4 w-4" aria-hidden />}>
          Nova melhoria
        </Button>
      </header>

      {criando && (
        <FormularioDeMelhoria
          aoSalvar={() => {
            setCriando(false);
            void qc.invalidateQueries({ queryKey: chavesCsi.lista() });
          }}
          aoCancelar={() => setCriando(false)}
        />
      )}

      <div className="flex gap-1 border-b border-border">
        {abas.map((aba) => (
          <button
            key={String(aba.valor)}
            type="button"
            onClick={() => setFiltro(aba.valor)}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              filtro === aba.valor
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {isPending ? (
        <SkeletonRegion label="Carregando melhorias">
          <Skeleton className="h-20 w-full rounded-lg" />
          <Skeleton className="h-20 w-full rounded-lg" />
        </SkeletonRegion>
      ) : !data?.items.length ? (
        <Card>
          <CardBody className="py-8 text-center text-sm text-muted-foreground">
            Nenhum registro de melhoria encontrado.
          </CardBody>
        </Card>
      ) : (
        <ul className="space-y-2">
          {data.items.map((m) => (
            <li key={m.id}>
              <LinhaDeItem
                item={m}
                aoAtualizar={() => void qc.invalidateQueries({ queryKey: chavesCsi.lista() })}
              />
            </li>
          ))}
        </ul>
      )}

      {data && (
        <p className="text-xs text-muted-foreground">
          {data.total} registro{data.total !== 1 ? 's' : ''} no total
        </p>
      )}
    </div>
  );
}

function FormularioDeMelhoria({
  item,
  aoSalvar,
  aoCancelar,
}: {
  item?: CsiImprovement;
  aoSalvar: () => void;
  aoCancelar: () => void;
}) {
  const [titulo, setTitulo] = useState(item?.title ?? '');
  const [descricao, setDescricao] = useState(item?.description ?? '');
  const [sourceType, setSourceType] = useState<CsiSourceType>(item?.sourceType ?? 'MANUAL');
  const [priority, setPriority] = useState<CsiPriority>(item?.priority ?? 'MEDIUM');
  const [dueAt, setDueAt] = useState(item?.dueAt ? item.dueAt.slice(0, 10) : '');
  const [erro, setErro] = useState('');

  const criacao = useMutation({
    mutationFn: () =>
      criarMelhoria({
        title: titulo.trim(),
        description: descricao.trim() || undefined,
        sourceType,
        priority,
        dueAt: dueAt ? new Date(`${dueAt}T00:00:00.000Z`).toISOString() : undefined,
      }),
    onSuccess: aoSalvar,
    onError: (e: Error) => setErro(e.message),
  });

  const edicao = useMutation({
    mutationFn: () =>
      atualizarMelhoria(item!.id, {
        title: titulo.trim(),
        description: descricao.trim(),
        priority,
        dueAt: dueAt ? new Date(`${dueAt}T00:00:00.000Z`).toISOString() : null,
      }),
    onSuccess: aoSalvar,
    onError: (e: Error) => setErro(e.message),
  });

  const pendente = criacao.isPending || edicao.isPending;

  return (
    <Card>
      <CardBody className="space-y-3 py-4">
        <h2 className="text-sm font-semibold">
          {item ? 'Editar melhoria' : 'Nova oportunidade de melhoria'}
        </h2>
        <div className="space-y-1">
          <label className="text-xs font-medium">Titulo</label>
          <input
            className={CLASSE_DE_CAMPO}
            placeholder="Descreva a oportunidade de melhoria"
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            autoFocus
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs font-medium">Descricao</label>
          <textarea
            className={CLASSE_DE_CAMPO}
            placeholder="Contexto, evidencias, impacto esperado..."
            rows={3}
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-3">
          {!item && (
            <div className="space-y-1">
              <label className="text-xs font-medium">Origem</label>
              <select
                className={`${CLASSE_DE_CAMPO} text-sm`}
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value as CsiSourceType)}
              >
                {ORIGENS.map((o) => (
                  <option key={o} value={o}>
                    {ORIGEM_PT[o]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="space-y-1">
            <label className="text-xs font-medium">Prioridade</label>
            <select
              className={`${CLASSE_DE_CAMPO} text-sm`}
              value={priority}
              onChange={(e) => setPriority(e.target.value as CsiPriority)}
            >
              <option value="HIGH">Alta</option>
              <option value="MEDIUM">Media</option>
              <option value="LOW">Baixa</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Prazo</label>
            <input
              type="date"
              className={`${CLASSE_DE_CAMPO} text-sm`}
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
            />
          </div>
        </div>
        {erro && <p className="text-xs text-destructive">{erro}</p>}
        <div className="flex gap-2">
          <Button
            size="sm"
            loading={pendente}
            disabled={!titulo.trim()}
            onClick={() => (item ? edicao.mutate() : criacao.mutate())}
          >
            {item ? 'Salvar' : 'Registrar'}
          </Button>
          <Button size="sm" variant="ghost" onClick={aoCancelar}>
            Cancelar
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function FormularioDeEncerramento({
  id,
  status,
  aoSalvar,
  aoCancelar,
}: {
  id: string;
  status: 'CLOSED' | 'REJECTED';
  aoSalvar: () => void;
  aoCancelar: () => void;
}) {
  const [nota, setNota] = useState('');
  const [erro, setErro] = useState('');

  const mutacao = useMutation({
    mutationFn: () =>
      atualizarMelhoria(id, {
        status,
        closureNote: nota.trim() || undefined,
      }),
    onSuccess: aoSalvar,
    onError: (e: Error) => setErro(e.message),
  });

  return (
    <div className="mt-3 rounded-lg border border-border bg-muted/30 p-3 space-y-2">
      <p className="text-xs font-medium">
        {status === 'CLOSED' ? 'Nota de conclusao (opcional)' : 'Motivo da rejeicao (opcional)'}
      </p>
      <textarea
        className={`${CLASSE_DE_CAMPO} text-xs`}
        rows={2}
        placeholder={
          status === 'CLOSED'
            ? 'O que foi feito, resultado alcancado...'
            : 'Por que esta melhoria nao sera implementada...'
        }
        value={nota}
        onChange={(e) => setNota(e.target.value)}
        autoFocus
      />
      {erro && <p className="text-xs text-destructive">{erro}</p>}
      <div className="flex gap-2">
        <Button size="sm" loading={mutacao.isPending} onClick={() => mutacao.mutate()}>
          {status === 'CLOSED' ? 'Concluir' : 'Rejeitar'}
        </Button>
        <Button size="sm" variant="ghost" onClick={aoCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}

function LinhaDeItem({
  item,
  aoAtualizar,
}: {
  item: CsiImprovement;
  aoAtualizar: () => void;
}) {
  const qc = useQueryClient();
  const [editando, setEditando] = useState(false);
  const [encerrando, setEncerrando] = useState<'CLOSED' | 'REJECTED' | null>(null);
  const [expandido, setExpandido] = useState(false);
  const [erroExclusao, setErroExclusao] = useState('');

  const avancar = useMutation({
    mutationFn: () =>
      atualizarMelhoria(item.id, {
        status: item.status === 'OPEN' ? 'IN_PROGRESS' : 'CLOSED',
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: chavesCsi.lista() }),
  });

  const excluir = useMutation({
    mutationFn: () => excluirMelhoria(item.id),
    onMutate: () => setErroExclusao(''),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: chavesCsi.lista() });
      aoAtualizar();
    },
    onError: (e: Error) => setErroExclusao(e.message),
  });

  const podeAvancar = item.status === 'OPEN' || item.status === 'IN_PROGRESS';
  const encerrado = item.status === 'CLOSED' || item.status === 'REJECTED';

  if (editando) {
    return (
      <FormularioDeMelhoria
        item={item}
        aoSalvar={() => {
          setEditando(false);
          aoAtualizar();
        }}
        aoCancelar={() => setEditando(false)}
      />
    );
  }

  return (
    <Card>
      <CardBody className="py-3">
        <div className="flex flex-wrap items-start gap-3">
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COR[item.status]}`}
              >
                {STATUS_PT[item.status]}
              </span>
              <span className={`text-xs ${PRIORIDADE_COR[item.priority]}`}>
                {PRIORIDADE_PT[item.priority]}
              </span>
              <span className="text-xs text-muted-foreground">
                {ORIGEM_PT[item.sourceType]}
              </span>
              {item.dueAt && !encerrado && (
                <span className="text-xs text-muted-foreground">
                  Prazo: {formatarData(item.dueAt)}
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">
                {formatarDataHora(item.createdAt)}
              </span>
            </div>

            <p className="text-sm font-medium text-foreground">{item.title}</p>

            {item.description && (
              <p className="text-xs text-muted-foreground">{item.description}</p>
            )}

            {encerrado && item.closureNote && (
              <p className="text-xs text-muted-foreground italic">
                {item.closureNote}
              </p>
            )}

            {encerrando && (
              <FormularioDeEncerramento
                id={item.id}
                status={encerrando}
                aoSalvar={() => {
                  setEncerrando(null);
                  aoAtualizar();
                }}
                aoCancelar={() => setEncerrando(null)}
              />
            )}

            {erroExclusao && <p className="text-xs text-destructive">{erroExclusao}</p>}

            {!encerrado && !editando && !encerrando && (
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                  onClick={() => setEditando(true)}
                >
                  Editar
                </button>
                {podeAvancar && item.status === 'IN_PROGRESS' && (
                  <button
                    type="button"
                    disabled={avancar.isPending}
                    className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    onClick={() => setEncerrando('CLOSED')}
                  >
                    Concluir
                  </button>
                )}
                {podeAvancar && (
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline-offset-2 hover:text-destructive hover:underline"
                    onClick={() => setEncerrando('REJECTED')}
                  >
                    Rejeitar
                  </button>
                )}
                <button
                  type="button"
                  disabled={excluir.isPending}
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-destructive hover:underline disabled:opacity-40"
                  onClick={() => excluir.mutate()}
                >
                  {excluir.isPending ? 'Excluindo…' : 'Excluir'}
                </button>
              </div>
            )}

            {encerrado && !editando && (
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  type="button"
                  disabled={excluir.isPending}
                  className="text-xs text-muted-foreground underline-offset-2 hover:text-destructive hover:underline disabled:opacity-40"
                  onClick={() => excluir.mutate()}
                >
                  {excluir.isPending ? 'Excluindo…' : 'Excluir'}
                </button>
              </div>
            )}
          </div>

          <div className="flex shrink-0 items-start gap-1 pt-0.5">
            {podeAvancar && !encerrando && (
              <>
                <button
                  type="button"
                  disabled={avancar.isPending}
                  onClick={() =>
                    item.status === 'IN_PROGRESS'
                      ? setEncerrando('CLOSED')
                      : avancar.mutate()
                  }
                  className="rounded p-1.5 text-success hover:bg-success/10 disabled:opacity-40"
                  title={item.status === 'OPEN' ? 'Marcar em andamento' : 'Marcar concluido'}
                >
                  <CheckCircle className="h-4 w-4" aria-hidden />
                </button>
                {item.status === 'OPEN' && (
                  <button
                    type="button"
                    className="rounded p-1.5 text-muted-foreground hover:bg-muted"
                    title="Rejeitar"
                    onClick={() => setEncerrando('REJECTED')}
                  >
                    <XCircle className="h-4 w-4" aria-hidden />
                  </button>
                )}
              </>
            )}
            <button
              type="button"
              className="rounded p-1.5 text-muted-foreground hover:bg-muted"
              title={expandido ? 'Recolher' : 'Expandir'}
              onClick={() => setExpandido((v) => !v)}
            >
              {expandido ? (
                <ChevronUp className="h-4 w-4" aria-hidden />
              ) : (
                <ChevronDown className="h-4 w-4" aria-hidden />
              )}
            </button>
          </div>
        </div>

        {expandido && (
          <div className="mt-3 border-t border-border pt-3 grid grid-cols-2 gap-2 text-xs text-muted-foreground sm:grid-cols-4">
            <div>
              <p className="font-medium text-foreground">Origem</p>
              <p>{ORIGEM_PT[item.sourceType]}</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Prioridade</p>
              <p className={PRIORIDADE_COR[item.priority]}>{PRIORIDADE_PT[item.priority]}</p>
            </div>
            {item.dueAt && (
              <div>
                <p className="font-medium text-foreground">Prazo</p>
                <p>{formatarData(item.dueAt)}</p>
              </div>
            )}
            {item.closedAt && (
              <div>
                <p className="font-medium text-foreground">Encerrado em</p>
                <p>{formatarDataHora(item.closedAt)}</p>
              </div>
            )}
            <div>
              <p className="font-medium text-foreground">Atualizado</p>
              <p>{formatarDataHora(item.updatedAt)}</p>
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  );
}
